
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Video, ExternalLink, Clock, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ZoomMeetingInfoProps {
  booking: {
    zoom_meeting_id?: string;
    zoom_join_url?: string;
    start_time: string;
    end_time: string;
    status: string;
  };
  userRole: 'client' | 'practitioner';
}

export const ZoomMeetingInfo: React.FC<ZoomMeetingInfoProps> = ({ 
  booking, 
  userRole 
}) => {
  const { toast } = useToast();

  const copyJoinUrl = () => {
    if (booking.zoom_join_url) {
      navigator.clipboard.writeText(booking.zoom_join_url);
      toast({
        title: "已复制",
        description: "会议链接已复制到剪贴板",
      });
    }
  };

  const openZoomMeeting = () => {
    if (booking.zoom_join_url) {
      // For mobile, open in same tab to avoid popup blockers
      if (window.innerWidth < 768) {
        window.location.href = booking.zoom_join_url;
      } else {
        window.open(booking.zoom_join_url, '_blank');
      }
    }
  };

  if (!booking.zoom_meeting_id || !booking.zoom_join_url) {
    return null;
  }

  const startTime = new Date(booking.start_time);
  const now = new Date();
  const canJoin = now >= new Date(startTime.getTime() - 15 * 60 * 1000); // 15分钟前可以加入

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Video className="w-5 h-5" />
          Zoom 视频会议
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Video className="w-3 h-3 mr-1" />
              会议已创建
            </Badge>
            {canJoin && (
              <Badge variant="default" className="bg-green-600">
                可以加入
              </Badge>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            会议 ID: {booking.zoom_meeting_id}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>
              {startTime.toLocaleString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={openZoomMeeting}
              disabled={!canJoin}
              className="flex-1"
              size="sm"
            >
              <Video className="w-4 h-4 mr-2" />
              {canJoin ? '加入会议' : '会议未开始'}
            </Button>
            <Button
              onClick={copyJoinUrl}
              variant="outline"
              size="sm"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>

          {!canJoin && (
            <p className="text-xs text-muted-foreground">
              会议开始前 15 分钟可以加入
            </p>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-sm text-blue-800">
            <strong>注意：</strong> 会议将自动录制并保存到云端。
            录音将被转录为文本记录以供后续查看。
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
